import React from 'react';
import { GridProps, ResponsiveColumns, SpacingSize, SpacingToken } from '../Grid.types';

const SPACING_TOKEN_MAP: Record<SpacingSize, SpacingToken> = {
    xxs: 'spacingVerticalXXS',
    xs: 'spacingVerticalXS',
    s: 'spacingVerticalS',
    m: 'spacingVerticalM',
    l: 'spacingVerticalL',
    xl: 'spacingVerticalXL',
    xxl: 'spacingVerticalXXL',
};

export const getSpacing = (size: SpacingSize, tokens: Record<SpacingToken, string>): string =>
    tokens[SPACING_TOKEN_MAP[size]];

export const getColumnTemplate = (cols: number | string): string => {
    if (typeof cols === 'string') return cols;
    return `repeat(${cols}, 1fr)`;
};

export type BuildGridStylesParams =
    Omit<GridProps, 'children' | 'as' | 'className' | 'onClick'> & {
        isResponsive: boolean;
        tokens: Record<SpacingToken, string>;
    };

export const buildGridStyles = ({
    columns,
    rows,
    gap,
    rowGap,
    columnGap,
    alignItems,
    justifyContent,
    justifyItems,
    alignContent,
    width,
    height,
    minHeight,
    maxHeight,
    style = {},
    isResponsive,
    tokens,
}: BuildGridStylesParams): React.CSSProperties => ({
    display: 'grid',

    ...(!isResponsive && {
        gridTemplateColumns: getColumnTemplate(columns as number | string),
    }),

    ...(rows && {
        gridTemplateRows: typeof rows === 'number'
            ? `repeat(${rows}, auto)`
            : rows,
    }),

    ...(gap && { gap: getSpacing(gap, tokens) }),
    ...(rowGap && { rowGap: getSpacing(rowGap, tokens) }),
    ...(columnGap && { columnGap: getSpacing(columnGap, tokens) }),

    ...(alignItems && { alignItems }),
    ...(justifyContent && { justifyContent }),
    ...(justifyItems && { justifyItems }),
    ...(alignContent && { alignContent }),

    ...(width && { width }),
    ...(height && { height }),
    ...(minHeight && { minHeight }),
    ...(maxHeight && { maxHeight }),

    ...style,
});

export const buildResponsiveStyles = (responsiveConfig: ResponsiveColumns): string => {
    const mobileGridTemplate = responsiveConfig.mobile
        ? getColumnTemplate(responsiveConfig.mobile)
        : '1fr';
    const tabletGridTemplate = responsiveConfig.tablet
        ? getColumnTemplate(responsiveConfig.tablet)
        : getColumnTemplate(responsiveConfig.desktop || 2);
    const desktopGridTemplate = responsiveConfig.desktop
        ? getColumnTemplate(responsiveConfig.desktop)
        : 'repeat(2, 1fr)';

    return `
    @media (max-width: 768px) {
      grid-template-columns: ${mobileGridTemplate};
    }
    @media (min-width: 769px) and (max-width: 1024px) {
      grid-template-columns: ${tabletGridTemplate};
    }
    @media (min-width: 1025px) {
      grid-template-columns: ${desktopGridTemplate};
    }
  `;
};
